import useCustomize from "@/components/newcustomize/hooks/useCustomize";
import { TProxyPlanKey } from "@/components/newcustomize/hooks/useProxyPlanKey";

import { useMemo } from "react";

export type TBandwidth = {
  bandwidth: number;
  name: string;
  isDefault?: boolean;
  isPopular?: boolean;
  description?: string;
};

const shared_default = [
  { bandwidth: 250, name: "Saver" },
  { bandwidth: 1000, name: "Plus" },
  {
    bandwidth: 5000,
    name: "Premium",
    isDefault: true,
    isPopular: true,
  },
  { bandwidth: 0, name: "Unlimited" },
];
const semidedicated_premium = [
  { bandwidth: 250, name: "Saver" },
  { bandwidth: 1000, name: "Plus" },
  {
    bandwidth: 5000,
    name: "Premium",
    isDefault: true,
    isPopular: true,
  },
  { bandwidth: 0, name: "Unlimited" },
];
const dedicated_premium = [
  { bandwidth: 250, name: "Saver" },
  { bandwidth: 1000, name: "Plus" },
  {
    bandwidth: 5000,
    name: "Premium",
    isDefault: true,
    isPopular: true,
  },
  { bandwidth: 0, name: "Unlimited" },
];
const shared_isp = [
  { bandwidth: 250, name: "Saver" },
  { bandwidth: 1000, name: "Plus" },
  {
    bandwidth: 5000,
    name: "Premium",
    isDefault: true,
    isPopular: true,
  },
  { bandwidth: 0, name: "Unlimited" },
];

const semidedicated_isp = [
  { bandwidth: 250, name: "Saver" },
  { bandwidth: 1000, name: "Plus" },
  {
    bandwidth: 5000,
    name: "Premium",
    isDefault: true,
    isPopular: true,
  },
  { bandwidth: 0, name: "Unlimited" },
];

const dedicated_isp = [
  { bandwidth: 250, name: "Saver" },
  { bandwidth: 1000, name: "Plus" },
  {
    bandwidth: 5000,
    name: "Premium",
    isDefault: true,
    isPopular: true,
  },
  { bandwidth: 0, name: "Unlimited" },
];
const hardcodedDefaults: Record<TProxyPlanKey, TBandwidth[]> = {
  shared_default,
  semidedicated_premium,
  dedicated_premium,
  shared_isp,
  semidedicated_isp,
  dedicated_isp,
  /**
   * The eight most purchased Rotating Residential tiers (2026 purchase data);
   * 1 GB is the default and the most popular. The top tier is 3000 GB, since
   * 5000 GB sits above the plan's stock and drew as out of stock.
   */
  shared_residential: [
    { bandwidth: 1, name: "Testing Out", isDefault: true, isPopular: true },
    { bandwidth: 3, name: "Micro" },
    { bandwidth: 10, name: "Mini" },
    { bandwidth: 25, name: "Starter" },
    { bandwidth: 100, name: "Advanced" },
    { bandwidth: 250, name: "Premium" },
    { bandwidth: 1000, name: "Enterprise" },
    { bandwidth: 3000, name: "Enterprise" },
  ],
  shared_verified: shared_default,
};

// This is a hook because we would like to edit it on the go with feature flags
export default function useBandwidth({
  onlyProxyPlan,
}: {
  /**
   * In some cases we do not want to make requests for all proxy type combinations
   * this allows us to limit what we are interested in
   */
  onlyProxyPlan: TProxyPlanKey;
}) {
  const mapping = useMemo(
    () => hardcodedDefaults[onlyProxyPlan] || [],
    [onlyProxyPlan]
  );

  const plansCustomizeResponse = useCustomize();

  const defaultsMapping = useMemo(() => {
    const { bandwidth_limit_max } = plansCustomizeResponse.data || {};
    if (typeof bandwidth_limit_max === "undefined") {
      return undefined;
    }

    // Find the default, but limit it to maximum value returned by the backend
    return mapping.find((item, index) => {
      // Limit to the max stock value
      if (index + 1 < mapping.length) {
        const nextBandWidth = mapping[index + 1].bandwidth;
        if (nextBandWidth > bandwidth_limit_max) {
          return true;
        }
      }
      return item.isDefault;
    })?.bandwidth;
  }, [mapping, plansCustomizeResponse]);

  return {
    mapping,
    defaultsMapping,
    isLoading: plansCustomizeResponse.isLoading,
  };
}
